import getConfig from 'next/config';

import { isSafeRegex } from 'commonUtils/safeRegex';

export const BASE_URL = getConfig()?.publicRuntimeConfig.baseUrl ?? '';
const ROOT = '/kredity-new';

// роуты для поисковой строки в браузере
export const WEB_ROUTES = {
  root: '/kredity-new',
  loansSeoLanding: '/kredity-new',
  creditCardsSeoLanding: '/promo/karty',
  loansRates: '/kredity/stavki-po-bankam',
  loansOffers: '/offers',
  newForm: '/anketa',
  landing: '/kredity/',
  newFormWL: '/anketa/wl',
  loansOffersWL: '/offers/wl',
  creditsFullDeal: '/full-deal',
  bezPposeshhenijaBanka: '/kredity/kredit_na-kartu-bez-poseshhenija-banka',
  obrazovanie: '/kredity/kredit_na-obrazovanie',
  iphonee: '/kredity/kredit_na-iphonee',
  nalichnymi: '/kredity/kredit-nalichnymi',
  wayback: '/wayback',
  product: `/bank/:bankAlias/product/:productAlias`,
  productForm: `/product/anketa`,
  productStatus: `/product/status`,
} as const;

export const generatePageRoute = (pageUrl?: string, withoutRoot?: boolean) => {
  const pattern = `^${withoutRoot ? '' : ROOT}${pageUrl ?? ''}/?$`;

  if (!isSafeRegex(pattern)) {
    throw new Error('Unsafe route regex detected');
  }

  return new RegExp(pattern);
};

// роуты-regexp для серверной части
export const LOANS_RATES_ROUTE = [generatePageRoute(WEB_ROUTES.loansRates, true)];
export const SEO_ROUTE = [
  generatePageRoute(WEB_ROUTES.bezPposeshhenijaBanka, true),
  generatePageRoute(WEB_ROUTES.obrazovanie, true),
  generatePageRoute(WEB_ROUTES.iphonee, true),
  generatePageRoute(WEB_ROUTES.nalichnymi, true),
];

export const MARKETPLACE_SEO_ROUTE = [generatePageRoute(WEB_ROUTES.landing, true)];
export const LOANS_SEO_ROUTE = [generatePageRoute(WEB_ROUTES.loansSeoLanding, true)];
export const CREDIT_CARDS_SEO_ROUTE = [generatePageRoute(WEB_ROUTES.creditCardsSeoLanding)];

export const USER_FORM_ROUTE = [generatePageRoute(WEB_ROUTES.newForm)];
export const USER_FORM_WL_ROUTE = [generatePageRoute(WEB_ROUTES.newFormWL)];
export const LOANS_OFFERS_ROUTE = [generatePageRoute(WEB_ROUTES.loansOffers)];
export const WAYBACK_ROUTE = [generatePageRoute(WEB_ROUTES.wayback)];
export const CREDITS_FULL_DEAL_ROUTE = [generatePageRoute(WEB_ROUTES.creditsFullDeal)];
export const PRODUCT_ROUTE = `${ROOT}${WEB_ROUTES.product}`;
export const PRODUCT_FORM_ROUTE = `${ROOT}${WEB_ROUTES.productForm}`;
export const PRODUCT_STATUS_ROUTE = `${ROOT}${WEB_ROUTES.productStatus}`;

// роуты next.js приложения (должны соответствовать файлам в /src/pages)
export const APP_ROUTES = {
  _root: ROOT,
  loansRates: `${ROOT}/rates`,
  loansOffers: `${ROOT}/offers`,
  newForm: `${ROOT}/form`,
  newFormWL: `${ROOT}/formWhiteLabel`,
  vitrins: `${ROOT}/vitrins`,
  loansSeoLanding: `${ROOT}/loansSeoLanding`,
  creditCardsSeoLanding: `${ROOT}/creditCardsSeoLanding`,
  fullDeal: `${ROOT}/creditFullDeal`,
  productForm: `${ROOT}/productForm`,
  productStatus: `${ROOT}/productStatus`,
  product: `${ROOT}/product`,
} as const;

// названия страниц для логики АБ тестов в приложении на клиенте (должны проставляться в контроллере страницы на сервере)
export const APP_PAGES_NAMES = {
  LOANS_RATES: 'loansRates',
  LOANS_OFFERS: 'loansOffers',
  NEW_FORM: 'newForm',
  NEW_FORM_WL: 'newFormWL',
  LOANS_OFFERS_RESTORE: 'loansOffersRestore',
  WAYBACK_RESTORE: 'waybackRestore',
  NEW_FORM_RESTORE: 'newFormRestore',
  CREDIT_FULL_DEAL: 'creditFullDeal',
  PRODUCT: 'product',
  PRODUCT_FORM: 'productForm',
  PRODUCT_STATUS: 'productStatus',
  VITRINS: 'vitrins',
  OBRAZOVANIE: 'kredit_na-obrazovanie',
  IPHONEE: 'kredit_na-iphonee',
  NALICHNYMI: 'kredit-nalichnymi',
  BEZ_POSESHHENIJA_BANKA: 'kredit_na-kartu-bez-poseshhenija-banka',
  MARKETPLACE_SEO_LANDING: 'marketplaceSeoLanding',
  LOANS_SEO_LANDING: 'loansSeoLanding',
  CREDIT_CARDS_SEO_LANDING: 'creditCardsSeoLanding',
  // в случае возвращения undefinedPage проверить контроллер server/controllers/pages/[страница].ts
  UNDEFINED_PAGE: 'undefinedPage',
} as const;

// конфигурация роутов для навигации в приложении на клиенте
export const APP_PAGES_CONFIG = {
  [APP_PAGES_NAMES.LOANS_RATES]: {
    app: APP_ROUTES.loansRates,
    web: WEB_ROUTES.loansRates,
  },
  [APP_PAGES_NAMES.LOANS_OFFERS]: {
    app: APP_ROUTES.loansOffers,
    web: `${WEB_ROUTES.root}${WEB_ROUTES.loansOffers}`,
  },
  [APP_PAGES_NAMES.LOANS_OFFERS_RESTORE]: {
    app: APP_ROUTES.loansOffers,
    web: `${WEB_ROUTES.root}${WEB_ROUTES.loansOffers}`,
  },
  [APP_PAGES_NAMES.WAYBACK_RESTORE]: {
    app: APP_ROUTES.loansOffers,
    web: `${WEB_ROUTES.root}${WEB_ROUTES.wayback}`,
  },
  [APP_PAGES_NAMES.NEW_FORM]: {
    app: APP_ROUTES.newForm,
    web: `${WEB_ROUTES.root}${WEB_ROUTES.newForm}`,
  },
  [APP_PAGES_NAMES.NEW_FORM_RESTORE]: {
    app: APP_ROUTES.newForm,
    web: `${WEB_ROUTES.root}${WEB_ROUTES.newForm}`,
  },
  [APP_PAGES_NAMES.NEW_FORM_WL]: {
    app: APP_ROUTES.newFormWL,
    web: `${WEB_ROUTES.root}${WEB_ROUTES.newFormWL}`,
  },
  [APP_PAGES_NAMES.OBRAZOVANIE]: {
    app: APP_ROUTES.vitrins,
    web: WEB_ROUTES.obrazovanie,
  },
  [APP_PAGES_NAMES.IPHONEE]: {
    app: APP_ROUTES.vitrins,
    web: WEB_ROUTES.iphonee,
  },
  [APP_PAGES_NAMES.NALICHNYMI]: {
    app: APP_ROUTES.vitrins,
    web: WEB_ROUTES.nalichnymi,
  },
  [APP_PAGES_NAMES.BEZ_POSESHHENIJA_BANKA]: {
    app: APP_ROUTES.vitrins,
    web: WEB_ROUTES.bezPposeshhenijaBanka,
  },
  [APP_PAGES_NAMES.CREDIT_FULL_DEAL]: {
    app: APP_ROUTES.fullDeal,
    web: `${WEB_ROUTES.root}${WEB_ROUTES.creditsFullDeal}`,
  },
  [APP_PAGES_NAMES.MARKETPLACE_SEO_LANDING]: {
    app: APP_ROUTES.vitrins,
    web: WEB_ROUTES.landing,
  },
  [APP_PAGES_NAMES.LOANS_SEO_LANDING]: {
    app: APP_ROUTES.loansSeoLanding,
    web: WEB_ROUTES.loansSeoLanding,
  },
  [APP_PAGES_NAMES.CREDIT_CARDS_SEO_LANDING]: {
    app: APP_ROUTES.creditCardsSeoLanding,
    web: WEB_ROUTES.creditCardsSeoLanding,
  },
  [APP_PAGES_NAMES.PRODUCT_FORM]: {
    app: APP_ROUTES.productForm,
    web: `${WEB_ROUTES.root}${WEB_ROUTES.productForm}`,
  },
  [APP_PAGES_NAMES.PRODUCT_STATUS]: {
    app: APP_ROUTES.productStatus,
    web: `${WEB_ROUTES.root}${WEB_ROUTES.productStatus}`,
  },
  [APP_PAGES_NAMES.PRODUCT]: {
    app: APP_ROUTES.product,
    web: `${WEB_ROUTES.root}${WEB_ROUTES.product}`,
  },
} as const;
